import { z } from "zod"

import {
  addDaysToDateKey,
  daysBetweenDateKeys,
  isKnownTimeZone,
  weekdayOnOrAfter,
  zonedDateKey,
  zonedHourOnDate,
} from "./zoned-time.js"

/**
 * When a client gets reminder emails about a request: on the days before
 * the due date the firm picks, on it, and every few days after it. The firm sets one default;
 * one client can have rules of their own. The same planner dates the emails
 * for the server and draws the example in the app, so the two never differ.
 */

export const reminderRuleLimits = {
  beforeDays: { min: 1, max: 30 },
  /** Reminders before the due date. */
  beforeCount: { max: 5 },
  everyDays: { min: 1, max: 14 },
  times: { min: 1, max: 5 },
} as const

const intBetween = (limit: { min: number; max: number }) =>
  z.number().int().min(limit.min).max(limit.max)

export const ReminderRulesSchema = z.object({
  /** False: no reminder emails. The firm can still send one by hand. */
  enabled: z.boolean(),
  /** One reminder per entry, that many days before the due date. Empty for
      none. Rules saved as one number (or null) read as a list of it. */
  beforeDays: z
    .array(intBetween(reminderRuleLimits.beforeDays))
    .max(reminderRuleLimits.beforeCount.max)
    .refine((list) => new Set(list).size === list.length, {
      message: "Each reminder needs its own day.",
    }),
  onDueDate: z.boolean(),
  /** Reminders after the due date, or null for none. */
  after: z
    .object({
      everyDays: intBetween(reminderRuleLimits.everyDays),
      times: intBetween(reminderRuleLimits.times),
    })
    .nullable(),
  /** A reminder that lands on a Saturday or Sunday goes out on Monday. */
  skipWeekends: z.boolean(),
})

export type ReminderRules = z.infer<typeof ReminderRulesSchema>

/** The firm's rules plus the hour and zone every reminder goes out at. */
export const PortalReminderConfigSchema = ReminderRulesSchema.extend({
  quietHour: z.number().int().min(0).max(23),
  timeZone: z.string().min(1).max(100),
})

export type PortalReminderConfig = z.infer<typeof PortalReminderConfigSchema>

/** What a firm may save: the zone must be one the runtime knows, or every
    reminder date would fail to compute. */
export const PortalReminderConfigBodySchema = PortalReminderConfigSchema.refine(
  (config) => isKnownTimeZone(config.timeZone),
  { message: "Choose a valid time zone.", path: ["timeZone"] }
)

/** One client's own rules. Null on the wire means "use the firm default". */
export const ReminderOverrideSchema = ReminderRulesSchema

/** 3 days before, on the due date, 3 days after, never on a weekend. */
export const defaultReminderRules: ReminderRules = {
  enabled: true,
  beforeDays: [3],
  onDueDate: true,
  after: { everyDays: 3, times: 1 },
  skipWeekends: true,
}

export const defaultPortalReminderConfig: PortalReminderConfig = {
  ...defaultReminderRules,
  quietHour: 8,
  timeZone: "America/Chicago",
}

// Stored shapes before 0080 ---------------------------------------------------

/** reminder_config before migration 0080. A row written by an older API
    during a rollout can still hold it. */
export type LegacyPortalReminderConfig = Readonly<{
  enabled: boolean
  cadence: "scaled" | "weekly" | "manual"
  cap: 1 | 2 | 3
  quietHour: number
  timeZone: string
}>

/** reminder_override before migration 0080. */
export type LegacyPortalReminderOverride = Readonly<
  | { enabled: false }
  | { enabled: true; cadence: "scaled" | "weekly"; cap: 1 | 2 | 3 }
>

/** Rules saved before the list of days before the due date: one number,
    or null for none. */
type SingleBeforeDay<T> = Omit<T, "beforeDays"> & { beforeDays: number | null }

export type StoredPortalReminderConfig =
  | PortalReminderConfig
  | SingleBeforeDay<PortalReminderConfig>
  | LegacyPortalReminderConfig

export type StoredReminderOverride =
  | ReminderRules
  | SingleBeforeDay<ReminderRules>
  | LegacyPortalReminderOverride

/**
 * The rules closest to an old cadence and cap. Migration 0080 runs the same
 * mapping in SQL; keep the two the same.
 *
 * - scaled (-3, 0, +3 days, cut to the cap): 3 days before, then on the
 *   due date, then 3 days after once.
 * - weekly (-14, -7, 0 days, cut to the cap): 1 reminder is 14 days
 *   before; 2 are 7 days before and on the due date; 3 add one more 7 days
 *   after, so the count and the weekly gap stay the same.
 * - manual: off.
 */
export const reminderRulesFromLegacy = (legacy: {
  enabled: boolean
  cadence?: string | null
  cap?: number | null
}): ReminderRules => {
  const cap = legacy.cap === 1 || legacy.cap === 2 ? legacy.cap : 3
  const enabled = legacy.enabled && legacy.cadence !== "manual"
  if (legacy.cadence === "weekly") {
    return {
      enabled,
      beforeDays: [cap === 1 ? 14 : 7],
      onDueDate: cap >= 2,
      after: cap === 3 ? { everyDays: 7, times: 1 } : null,
      skipWeekends: true,
    }
  }
  return {
    enabled,
    beforeDays: [3],
    onDueDate: cap >= 2,
    after: cap === 3 ? { everyDays: 3, times: 1 } : null,
    skipWeekends: true,
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const hourOf = (value: unknown) =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 23
    ? value
    : defaultPortalReminderConfig.quietHour

const zoneOf = (value: unknown) =>
  typeof value === "string" && value.length > 0
    ? value
    : defaultPortalReminderConfig.timeZone

/** Rules from any stored shape: the current one, the pre-0080 one, or
    something unreadable (the default). */
const rulesFromStored = (stored: Record<string, unknown>): ReminderRules => {
  const current = ReminderRulesSchema.safeParse(stored)
  if (current.success) return current.data
  if (typeof stored.beforeDays === "number" || stored.beforeDays === null) {
    const single = ReminderRulesSchema.safeParse({
      ...stored,
      beforeDays: stored.beforeDays === null ? [] : [stored.beforeDays],
    })
    if (single.success) return single.data
  }
  if ("cadence" in stored || "cap" in stored) {
    return reminderRulesFromLegacy({
      enabled: stored.enabled !== false,
      cadence: typeof stored.cadence === "string" ? stored.cadence : null,
      cap: typeof stored.cap === "number" ? stored.cap : null,
    })
  }
  return {
    ...defaultReminderRules,
    enabled: stored.enabled !== false,
  }
}

/** The firm's reminder config from the stored jsonb, in either shape. */
export const reminderConfigFromStored = (
  stored: StoredPortalReminderConfig | null | undefined
): PortalReminderConfig => {
  if (!isObject(stored)) return defaultPortalReminderConfig
  return {
    ...rulesFromStored(stored),
    quietHour: hourOf(stored.quietHour),
    timeZone: zoneOf(stored.timeZone),
  }
}

/** One client's own rules from the stored jsonb, or null for the default. */
export const reminderOverrideFromStored = (
  stored: StoredReminderOverride | null | undefined
): ReminderRules | null => (isObject(stored) ? rulesFromStored(stored) : null)

/** The rules in effect for one client: their own, or the firm's. */
export const effectiveReminderRules = (
  config: StoredPortalReminderConfig,
  override: StoredReminderOverride | null | undefined
): ReminderRules => {
  const own = reminderOverrideFromStored(override)
  if (own) return own
  const { quietHour: _hour, timeZone: _zone, ...rules } =
    reminderConfigFromStored(config)
  return rules
}

// Planner ---------------------------------------------------------------------

export const reminderSlots = [
  "before",
  "before_2",
  "before_3",
  "before_4",
  "before_5",
  "due",
  "after_1",
  "after_2",
  "after_3",
  "after_4",
  "after_5",
] as const

/** Which rule a send comes from. With the request id and the recipient it
    names one email, so a re-plan never sends the same reminder twice. */
export type ReminderSlot = (typeof reminderSlots)[number]

/** 5 + 1 + 5: up to five before, on the due date, and up to five after. */
export const maxReminderSends = reminderSlots.length

export type PlannedReminderSend = Readonly<{
  slot: ReminderSlot
  /** 1 for the first email of the plan. */
  ordinal: number
  /** The plan's final email. */
  last: boolean
  /** The local day it goes out, as YYYY-MM-DD. */
  date: string
  scheduledAt: Date
  /** Days from the due date to `date`: negative before, 0 on the day. */
  offsetDays: number
  /** It fell on a weekend and moved to Monday. */
  movedFromWeekend: boolean
}>

export type ReminderPlanInput = Readonly<{
  /** When the request went out. Nothing is sent on or before that day: the
      request email is that day's email. */
  sentAt: Date
  dueAt: Date
  timeZone: string
  /** Local hour, 0-23, every reminder goes out at. */
  sendHour: number
}>

const clamp = (value: number, limit: { min: number; max: number }) =>
  Math.min(limit.max, Math.max(limit.min, Math.round(value)))

/** The days before the due date reminders go out, first to last: each
    held to the limits, each once. "[3, 7]" is 7, then 3. */
export const beforeDueOffsets = (
  rules: Pick<ReminderRules, "beforeDays">
): number[] =>
  [
    ...new Set(
      rules.beforeDays
        .slice(0, reminderRuleLimits.beforeCount.max)
        .map((days) => clamp(days, reminderRuleLimits.beforeDays))
    ),
  ].sort((left, right) => right - left)

/**
 * The reminder emails one request gets, in date order. Every date is a
 * local day in `timeZone`; every email goes out at `sendHour` that day.
 *
 * - A send on or before the day the request went out is dropped.
 * - With `skipWeekends`, a Saturday or Sunday send moves to Monday.
 * - Sends that land on the same day become one email. The one that did not
 *   move keeps the day; otherwise the earlier rule does.
 * - At most 11: 5 before, on the due date, and 5 after.
 *
 * It does not know what time it is. The scheduler drops sends already in
 * the past; the app shows them all.
 */
export const planReminderSends = (
  rules: ReminderRules,
  input: ReminderPlanInput
): PlannedReminderSend[] => {
  if (!rules.enabled) return []
  const dueDate = zonedDateKey(input.dueAt, input.timeZone)
  const sentDate = zonedDateKey(input.sentAt, input.timeZone)
  const sendHour = Math.min(23, Math.max(0, Math.floor(input.sendHour)))

  const wanted: Array<{ slot: ReminderSlot; offset: number }> = []
  beforeDueOffsets(rules).forEach((daysBefore, index) => {
    wanted.push({
      slot: (index === 0 ? "before" : `before_${index + 1}`) as ReminderSlot,
      offset: -daysBefore,
    })
  })
  if (rules.onDueDate) wanted.push({ slot: "due", offset: 0 })
  if (rules.after) {
    const every = clamp(rules.after.everyDays, reminderRuleLimits.everyDays)
    const times = clamp(rules.after.times, reminderRuleLimits.times)
    for (let time = 1; time <= times; time += 1) {
      wanted.push({
        slot: `after_${time}` as ReminderSlot,
        offset: every * time,
      })
    }
  }

  const candidates = wanted
    .map((send, order) => {
      const natural = addDaysToDateKey(dueDate, send.offset)
      const date = rules.skipWeekends ? weekdayOnOrAfter(natural) : natural
      return { ...send, order, date, moved: date !== natural }
    })
    .filter((send) => send.date > sentDate)
    .sort(
      (left, right) =>
        left.date.localeCompare(right.date) ||
        Number(left.moved) - Number(right.moved) ||
        left.order - right.order
    )

  const kept = candidates.filter(
    (send, index) => index === 0 || candidates[index - 1]!.date !== send.date
  )
  return kept.map((send, index) => ({
    slot: send.slot,
    ordinal: index + 1,
    last: index === kept.length - 1,
    date: send.date,
    scheduledAt: zonedHourOnDate(send.date, sendHour, input.timeZone),
    offsetDays: daysBetweenDateKeys(dueDate, send.date),
    movedFromWeekend: send.moved,
  }))
}
